import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";

const STAT_PAIRS = [
  ["possession", "possession_home", "possession_away", "Ballbesitz", "%"],
  ["shots", "shots_home", "shots_away", "Schüsse", ""],
  ["shots_on_target", "shots_on_target_home", "shots_on_target_away", "Schüsse aufs Tor", ""],
  ["passes", "passes_home", "passes_away", "Pässe", ""],
  ["pass_accuracy", "pass_accuracy_home", "pass_accuracy_away", "Passgenauigkeit", "%"],
  ["corners", "corners_home", "corners_away", "Eckbälle", ""],
  ["fouls", "fouls_home", "fouls_away", "Fouls", ""],
  ["yellow", "yellow_home", "yellow_away", "Gelbe Karten", ""],
  ["red", "red_home", "red_away", "Rote Karten", ""],
  ["offsides", "offsides_home", "offsides_away", "Abseits", ""],
  ["tackles", "tackles_home", "tackles_away", "Tacklings", ""],
  ["interceptions", "interceptions_home", "interceptions_away", "Interceptions", ""],
  ["saves", "saves_home", "saves_away", "Paraden", ""],
  ["xg", "xg_home", "xg_away", "xG (Expected Goals)", ""],
  ["xa", "xa_home", "xa_away", "xA (Expected Assists)", ""],
];

function Bar({ left, right, leftColor = "#29E9FF", rightColor = "#C247FF" }) {
  const total = (left || 0) + (right || 0);
  const lp = total > 0 ? (left / total) * 100 : 50;
  return (
    <div className="flex h-2 bg-white/5 rounded-sm overflow-hidden">
      <div style={{ width: `${lp}%`, background: leftColor }} />
      <div style={{ width: `${100 - lp}%`, background: rightColor }} />
    </div>
  );
}

export default function MatchAnalysis() {
  const { id } = useParams();
  const [m, setM] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/matches/${id}`).then((r) => setM(r.data)).catch((e) => setError(formatApiError(e)));
  }, [id]);

  if (error) return <div className="text-red-400 text-sm font-mono">{error}</div>;
  if (!m) return <div className="font-mono text-xs text-white/40">Loading…</div>;

  const stats = m.stats || {};
  const events = m.events || [];
  const goals = events.filter((e) => e.type === "goal").sort((a, b) => a.minute - b.minute);

  return (
    <div className="space-y-8 fade-in" data-testid="match-analysis-page">
      <Link to={`/live/${id}`} className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 hover:text-primary link-underline">← Match</Link>

      <div className="card-solid p-6 tracing-border">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-gradient mb-2">Match Analysis</div>
        <h1 className="font-display text-3xl tracking-tight">
          <span className="text-primary">{m.home_name}</span> {m.home_score} · {m.away_score}{" "}
          <span className="text-magenta">{m.away_name}</span>
        </h1>
        <div className="font-mono text-xs text-white/50 mt-2">
          {m.home_team} vs {m.away_team} · {new Date(m.start_time).toLocaleString("de-DE")}
        </div>
      </div>

      {/* Goal timeline */}
      <div className="card-solid p-5">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-gradient mb-4">Goal Timeline</div>
        <div className="relative h-12 bg-white/5 rounded-sm overflow-hidden">
          {[15, 30, 45, 60, 75, 90].map((mn) => (
            <div key={mn} className="absolute top-0 bottom-0 border-l border-white/10" style={{ left: `${(mn / 90) * 100}%` }}>
              <div className="absolute -bottom-5 -translate-x-1/2 font-mono text-[9px] text-white/30">{mn}'</div>
            </div>
          ))}
          {goals.map((g) => (
            <div
              key={g.id}
              title={`${g.minute}' ${g.player || ""} (${g.team})`}
              className="absolute top-1 w-3 h-10 -translate-x-1/2 rounded-sm"
              style={{
                left: `${(Math.min(g.minute, 90) / 90) * 100}%`,
                background: g.team === "home" ? "#29E9FF" : "#C247FF",
              }}
            />
          ))}
        </div>
      </div>

      {/* Stats */}
      <div className="card-solid p-5">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-gradient mb-4">Match Stats</div>
        <div className="grid grid-cols-3 mb-4 items-center text-sm">
          <div className="font-display text-primary text-xl">{m.home_name}</div>
          <div className="text-center font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">vs</div>
          <div className="font-display text-magenta text-xl text-right">{m.away_name}</div>
        </div>
        <div className="space-y-3">
          {STAT_PAIRS.map(([k, lh, lr, label, unit]) => {
            const lh_v = stats[lh] || 0;
            const lr_v = stats[lr] || 0;
            return (
              <div key={k} className="space-y-1">
                <div className="grid grid-cols-3 items-center text-sm">
                  <div className="font-mono tabular-nums text-primary">{lh_v}{unit}</div>
                  <div className="text-center font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">{label}</div>
                  <div className="font-mono tabular-nums text-magenta text-right">{lr_v}{unit}</div>
                </div>
                <Bar left={lh_v} right={lr_v} />
              </div>
            );
          })}
        </div>
      </div>

      {/* Events */}
      <div className="card-solid p-5">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-gradient mb-4">Alle Events</div>
        {events.length === 0 ? (
          <div className="text-white/40 font-mono text-xs uppercase tracking-[0.2em] py-6 text-center">Keine Events.</div>
        ) : (
          <div className="space-y-1.5 text-sm font-mono">
            {events.slice().sort((a, b) => a.minute - b.minute).map((e) => (
              <div key={e.id} className="flex gap-3 items-center">
                <span className="tabular-nums w-10 text-white/50">{e.minute}'</span>
                <span className="uppercase tracking-[0.2em] text-[10px] w-20 text-white/60">{e.type}</span>
                <span className="flex-1">{e.player || "—"}</span>
                <span className={`text-[10px] uppercase tracking-[0.2em] ${e.team === "home" ? "text-primary" : "text-magenta"}`}>{e.team}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
